import isEqual from 'lodash'
import {Injectable} from '@angular/core'
import {DataService} from '@app/core/services/data/data.service'
import {
  ElasticQuery,
  Data,
  TermFilter,
  RangeFilter,
  ElasticSort,
  TermsAggregation,
  Aggregation,
  ApiDocument,
  ElasticFilter,
} from '@app/core/services/data.model'
import {Observable, of, combineLatest} from 'rxjs'
import {map, first, retry, catchError, tap} from 'rxjs/operators'
import {LoggerService} from '@app/core/services/logger.service'

export interface queryCacheObject {
  query: ElasticQuery
  result: Data
  timestamp: number
}

export interface DataResponse {
  items: ApiDocument[]
  count: number
}
@Injectable({
  providedIn: 'root',
})
export class TerminalEventsDataService {
  private sorts: ElasticSort[] = [{field: 'ingestSequence', asc: true}]

  private queryCacheStack: queryCacheObject[] = []
  private queryCacheSize = 10
  private disableCaching = true
  // 30 secs lifetime
  private queryCacheLifeTime = 30000

  constructor(
    private dataSvc: DataService,
    private logger: LoggerService,
  ) {}

  retrieveData(
    query: ElasticQuery,
    sorts: ElasticSort[],
  ): Observable<DataResponse> {
    query.sorts = sorts
    // undefined is falsy
    const asc = sorts.find((filt) => {
      return filt.field === 'ingestSequence' && filt.asc === true
    })
    return this.queryData(query).pipe(
      first(),
      tap((response) => {
        if (!this.disableCaching) {
          this.updateQueryCache(query, response)
        }
      }),
      map((res: Data) => {
        let returnObject: any = {}
        if (asc) {
          // remove zero index because the asc false query supplies that one
          returnObject = {
            items: res.documents
              .map((item, index) => {
                item.scrollIndex = (query.offset + index) * -1
                return item
              })
              .reverse(),
            count: res.count,
          }
        } else {
          returnObject = {
            items: res.documents.map((item, index) => {
              item.scrollIndex = query.offset + index
              return item
            }),
            count: res.count,
          }
        }
        // only pass the bucket object when we are dealing with sourceNames
        if (res.aggregations.sourceName) {
          returnObject['buckets'] = res.aggregations.sourceName.buckets
        }
        if (res.aggregations.level) {
          returnObject['buckets'] = res.aggregations.level.buckets
        }
        return returnObject
      }),
      retry(1), // retry a failed request up to 1 time
      catchError((e) => {
        this.logger.log('catched http error: ', e)
        return of({items: [], count: 0})
      }),
    )
  }

  updateQueryCache(query: ElasticQuery, response: Data) {
    // first filter from array
    this.queryCacheStack = this.queryCacheStack.filter((value, index) => {
      return index < this.queryCacheSize - 1
    })
    this.queryCacheStack.unshift({
      query: query,
      result: response,
      timestamp: Date.now(),
    })
  }

  queryData(query: ElasticQuery): Observable<Data> {
    if (!this.disableCaching) {
      const q = this.queryCacheStack.find((cached) => {
        return (
          isEqual(cached.query, query) &&
          Date.now() - this.queryCacheLifeTime < cached.timestamp
        )
      })
      if (q) {
        console.log('returning cached response for :', query)
        return of(q.result)
      }
    }
    return this.dataSvc.getData(query)
  }

  retrieveAggregations(
    query: ElasticQuery,
    eventName: string,
  ): Observable<{name: string; key: string; count: string}[]> {
    query.sorts = this.sorts
    return this.dataSvc.getData(query).pipe(
      first(),
      map((res: Data) => {
        return res.aggregations[eventName].buckets
      }),
    )
  }

  createAggregationsQuery(
    aggregations: Aggregation[] = [],
    filters: ElasticFilter[] = [],
    eventName: string,
  ) {
    // just a query with size: 0 to optimize
    const q = new ElasticQuery(eventName, '', 0)
    filters.forEach((filter) => {
      q.addFilter(filter)
    })
    aggregations.forEach((aggr) => {
      q.addAggregation(aggr)
    })
    q['size'] = 0
    return q
  }

  createQuery(
    {
      count = 25,
      filters,
      query = '',
      offset = 0,
    }: {
      count: number
      filters: (TermFilter | RangeFilter)[]
      query?: string
      offset?: number
    },
    eventName: string,
  ) {
    const q = new ElasticQuery(eventName, query, count)
    q.offset = offset
    filters.forEach((filter) => {
      q.addFilter(filter)
    })
    return q
  }

  createQueryHelper(
    start: number,
    end: number,
    filters,
    query = '',
    ingestSequence: number | null = null,
    eventName: string,
  ) {
    if (end < start) {
      throw new Error('the end index should be bigger then the start index')
    }
    // first get by ingestSequence (or nearest)
    if (!ingestSequence) {
      // first query for this serialNumber scrollIndex 0, offset is for virtual scroll
      const q = this.createQuery(
        {count: end - start, filters: filters, query: query, offset: start},
        eventName,
      )
      return [{q: q, asc: false}]
    } else {
      // these both need a ingestSequence to know where to start from
      if (start >= 0) {
        // get only backward in time (asc false)
        const q = this.createQuery(
          {count: end - start, filters: filters, query: query, offset: start},
          eventName,
        )
        q.query += ` +ingestSequence:[0 TO ${ingestSequence}]`
        return [{q: q, asc: false}]
      } else if (end <= 0) {
        // get only forward in time (asc true)
        const q = this.createQuery(
          {
            count: Math.abs(start) - Math.abs(end),
            filters: filters,
            query: query,
            offset: Math.abs(end) + 1,
          },
          eventName,
        )
        q.query += ` +ingestSequence:[${ingestSequence} TO *]`
        return [{q: q, asc: true}]
      } else {
        // both before and after
        // the 0 and after bit
        const qAfter = this.createQuery(
          {count: start * -1, filters: filters, query: query, offset: 0},
          eventName,
        )

        qAfter.query += ` +ingestSequence:[${ingestSequence} TO *]`
        // the before 0 bit:
        const qBefore = this.createQuery(
          {count: end, filters: filters, query: query, offset: 1},
          eventName,
        )

        qBefore.query += ` +ingestSequence:[0 TO ${ingestSequence}]`
        return [
          {q: qBefore, asc: false},
          {q: qAfter, asc: true},
        ]
      }
    }
  }

  // pass start and end, calculate queries from there
  // TODO: refactor, this ain't DRY
  getData(
    start: number,
    end: number,
    filters,
    query = '',
    ingestSequence: number | null = null,
    listConfig: any,
  ) {
    const res = this.createQueryHelper(
      start,
      end,
      filters,
      query,
      ingestSequence,
      listConfig.eventName,
    )
    res[0].q.aggregations = [
      new TermsAggregation(
        listConfig.convenienceFilter,
        listConfig.convenienceFilter,
        25,
      ),
    ]

    if (res.length === 1) {
      return this.retrieveData(res[0].q, [
        {field: 'ingestSequence', asc: res[0].asc},
      ])
    } else if (res.length === 2) {
      return combineLatest(
        this.retrieveData(res[0].q, [
          {field: 'ingestSequence', asc: res[0].asc},
        ]),
        this.retrieveData(res[1].q, [
          {field: 'ingestSequence', asc: res[1].asc},
        ]),
      ).pipe(
        map(([before, after]) => {
          return {items: [...before.items, ...after.items], count: after.count}
        }),
      )
    }
  }

  // yeah just copy it and make it work as we want it to, spaghetti with meatballs
  getAllData(
    start: number,
    end: number,
    filters,
    query = '',
    ingestSequence: number | null = null,
    listConfig: any,
  ) {
    const allDataQuery = this.createQuery(
      {count: 10000, filters: filters, query: query, offset: 0},
      listConfig.eventName,
    )

    return this.retrieveData(allDataQuery, [
      {field: 'ingestSequence', asc: true},
    ])
  }
}
